import * as i0 from '@angular/core';
import { Injectable, NgModule, EventEmitter } from '@angular/core';
import * as i1 from '@angular/common/http';
import { of } from 'rxjs';
import { map, share, tap } from 'rxjs/operators';
import * as i3 from '@og_soft/cache';
import { CachingInterceptor } from '@og_soft/cache';

class DataBaseServiceConfig {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: DataBaseServiceConfig, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: DataBaseServiceConfig, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: DataBaseServiceConfig, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * info k zakomentovani:
 * v angularu by se nemeli pouzivat HttpClientModule jinde nez v root module
 * je to proto ze v root module si ho aplikace nastavi a pote chcem do modulu
 * dostat injectovanou instanci root komponenty a ne aby si moduly udělali
 * vlastní instanci HttpClientModule nad kterou nemá aplikace kontrolu
 */
//exports: [HttpClientModule]
class OgDataBaseModule {
    static forRoot(config) {
        return {
            ngModule: OgDataBaseModule,
            providers: [
                {
                    provide: DataBaseServiceConfig,
                    useValue: config,
                },
                // CacheModule.forRoot().providers,
                // {provide: HTTP_INTERCEPTORS, useClass: CachingInterceptor, multi: true},
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: OgDataBaseModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.7", ngImport: i0, type: OgDataBaseModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: OgDataBaseModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: OgDataBaseModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                    // HttpClientModule,
                    // CacheModule
                    ],
                    exports: [
                    /*HttpClientModule, CacheModule*/
                    ],
                    providers: [
                    // {provide: HTTP_INTERCEPTORS, useClass: CachingInterceptor, multi: true}
                    ],
                }]
        }] });

/**
 * Bázová třída pro služby pro komunikaci s API
 *
 * ## Příklad odvozené třídy:
 *
 * ```ts
 * \@Injectable({
 * 	 providedIn: 'root'
 * })
 * export class DataUnicredit extends DataBase<RecUnicredit> {
 *   // URL endpointu, který použije metoda getall
 *   protected getAllUrl(): string | undefined {
 *     return 'eu-unicredit';
 *   }
 *
 * 	 // Workaround nedostatku v Angularu - bez toho se injectuje bázová třída místo odvozené.
 *   static ngInjectableDef = undefined;
 * }
 * ```
 */
class DataBase {
    /**
     * @ignore
     */
    constructor(http, configService, cache) {
        this.http = http;
        this.configService = configService;
        this.cache = cache;
        this.urlExpandParams = null;
        /**
         * Bázová URL API.
         * Toto se nastavuje v konstruktoru na základě injektovaného configu
         */
        this.baseurl = undefined;
        /**
         * Cache pro jednotlivé requesty (nikoliv data!). Zabraňuje zasílání stejných requestů opakovaně. Jsou to dvě cache,
         * jedna pro getall a druhá pro getone metody.
         */
        this.cacheAll = {};
        this.cacheOne = {};
        this.cacheSingleton = {};
        this.cacheOptions = {};
        /**
         * Event, který se vyvolá, když se načtou všechna data. Potomci, kteří vytvářejí compound service jej vyvolají
         * v okamžiku, kdy se do dat doplní další části záznamů.
         */
        this.onDataCollected = new EventEmitter();
        /**
         * Event, který se vyvolá při změně jakýchkoliv dat.
         */
        this.onDataChanged = new EventEmitter();
        this.configureUrl(configService);
    }
    /**
     * Vrací jeden objekt (detail např. pro editaci ve formuláři)
     *
     * @param id Id
     * @param params   Objekt s parametry, které budou přidány k URL jako query string.
     * @returns Data vrácená z API.
     */
    getOneUrl(id, params) {
        // V případech, kdy nemám id nadefinované, nechci zbytečně posílat request.
        // Nedává smysl volat getAllUrl s nenastaveným id, protože v endpointu to potom volá get místo getone
        if (id !== undefined && id !== null) {
            const url = this.getAllUrl(params);
            if (url === undefined) {
                console.error(`Chyba: třída ${this.constructor.name} metodu getOneUrl neumí - nemá definovanou vlastnost getAllUrl`);
                return undefined;
            }
            return `${url}` + `/${id}`;
        }
        return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    getParamsHash(params) {
        const result = [];
        for (const key in params) {
            // eslint-disable-next-line no-prototype-builtins
            if (params.hasOwnProperty(key)) {
                result.push(key + '=' + params[key]);
            }
        }
        if (result.length) {
            return result.join(',');
        }
        else {
            return '__no_params__';
        }
    }
    /**
     * Metoda, která umožňuje některým servisám nastavit url, ze kterého mají vracet data.
     * V případě potřeby je přetížená v konkrétních třídách (typicky v servisách na procesy)
     *
     */
    configureUrl(config) {
        this.baseurl = config.appUrl;
    }
    /**
     * Vrací množinu dat např. pro tabulku (potenciálně stránkovaných).
     *
     * @param params   Objekt s parametry, které budou přidány k URL jako query string. Případně, pokud je nastaveno
     * posílání getAll požadavku přes POST, jsou parametry poslány v těle zprávy.
     * @param cacheTimeout      Pocet sekund do expirace cache
     * @param urlExpandParams Objekt parametrů url adresy k nahrazení v this.getAllUrl(),
     * např. `data/recipes/${this.urlExpandParams.recipeId}/params`;
     * @returns GetallResult  Data vrácená z API.
     */
    getall(params, cacheTimeout, urlExpandParams) {
        this.urlExpandParams = urlExpandParams;
        return this.getallRaw(params, cacheTimeout)
            .pipe(map((d) => {
            return this.dataPostprocess(d);
        }))
            .pipe(share());
    }
    /**
     * Vrací množinu dat např. pro tabulku (potenciálně stránkovaných) z API, bez provedení transformací dataPostprocess.
     *
     * @param params   Objekt s parametry, které budou přidány k URL jako query string. Případně, pokud je nastaveno
     * posílání getAll požadavku přes POST, jsou parametry poslány v těle zprávy.
     * @param cacheTimeout      Pocet sekund do expirace cache
     * @private
     * @returns GetallResult  Data vrácená z API.
     */
    getallRaw(params, cacheTimeout) {
        const url = this.getAllUrl(params);
        if (url === undefined) {
            console.error(`Chyba: třída ${this.constructor.name} metodu getall neumí - nemá definovanou vlastnost getallUrl`);
            return undefined;
        }
        const hash = this.getParamsHash(params);
        if (!this.cacheAll[hash]) {
            if (this.getAllParamsAsPost()) {
                console.log('Using POST request instead of Get');
                const opts = {
                    headers: { [CachingInterceptor.CACHE_HEADER]: cacheTimeout + '' },
                };
                this.cacheAll[hash] = this.http
                    .post(`${this.baseurl}/${url}?extended=1`, params, opts)
                    .pipe(tap((d) => {
                    delete this.cacheAll[hash];
                }))
                    .pipe(share());
            }
            else {
                const opts = {
                    params: { ...params },
                    headers: { [CachingInterceptor.CACHE_HEADER]: cacheTimeout + '' },
                };
                this.cacheAll[hash] = this.http
                    .get(`${this.baseurl}/${url}`, opts)
                    .pipe(tap((d) => {
                    delete this.cacheAll[hash];
                }))
                    .pipe(share());
            }
        }
        return this.cacheAll[hash];
    }
    delete(id, params, urlExpandParams) {
        this.urlExpandParams = urlExpandParams;
        const url = this.getAllUrl(params);
        if (url === undefined) {
            console.error(`Chyba: třída ${this.constructor.name} metodu delete neumí - nemá definovanou vlastnost getallUrl`);
            return undefined;
        }
        const opts = {
            params: { ...params },
        };
        this.clearCache();
        return this.http
            .delete(`${this.baseurl}/${url}` + (id ? `/${id}` : ``), opts)
            .pipe(tap((x) => this.dataChanged()));
    }
    /**
     * Umožňuje upravovat data načtená službou. Například podle ID služby načíst další detaily služby, podle ID zákazníka
     * detaily zákazníka, atd.
     * @param data Data jak byla načtena z API.
     * @returns GetallResult Data doplněná o transformované hodnoty dle potřeby konkrétní implementace služby.
     */
    dataPostprocess(data) {
        data.filterInfo = this.metaFetchFilterInfo(data.meta);
        data.data = data.data.map((r) => this.recordPostprocess(r));
        return data;
    }
    /**
     * Z poslaných metadat popisujících, co je vyselectováno sestavuje informaci o tom jak je vyfiltrováno. Tuto textovou
     * informaci pak následně mohou používat zobrazovací komponenty. Výsledkem je pole jednotlivých řetězců, které pak
     * komponenta filtrů dokáže zobrazit. Do jednoho řetězce dávejte to, co k sobě logicky patří. Např. pokud se filtruje
     * datum od-do.
     * V potomkových třídách v přetížení této metody je implementováno naformátování těchto řetězců.
     * @returns string[] Jednotlivé naformátované nastavené hodnoty filtrů (zobrazují se jako info o vyfiltrování).
     * @param meta
     */
    metaFetchFilterInfo(meta) {
        return undefined;
    }
    /**
     * Vrací jeden objekt (detail např. pro editaci ve formuláři)
     *
     * @param id Id
     * @param params   Objekt s parametry, které budou přidány k URL jako query string.
     * @param cacheTimeout      Pocet sekund do expirace cache
     * @param options HTTP options - Pozor, nelze poslat všechny options (např. headers, viz kód níže). Přidal jsem kvůli reportProgress
     * @param urlExpandParams urlExpandParams
     * @returns Data vrácená z API.
     */
    getone(id, params, cacheTimeout, options, urlExpandParams) {
        this.urlExpandParams = urlExpandParams;
        // V případech, kdy nemám id nadefinované, nechci zbytečně posílat request.
        // Nedává smysl volat getone s nenastaveným id, protože v endpointu to potom volá get místo getone
        if (id !== undefined && id !== null) {
            return this.getoneRaw(id, params, cacheTimeout, options)
                .pipe(map((d) => {
                const res = d;
                return this.recordPostprocess(res);
            }))
                .pipe(share());
        }
        return of(null);
    }
    /**
     * Vrací jeden objekt (detail např. pro editaci ve formuláři)
     *
     * @param id Id
     * @param params   Objekt s parametry, které budou přidány k URL jako query string.
     * @param cacheTimeout      Pocet sekund do expirace cache
     * @param options HTTP options - Pozor, nelze poslat všechny options (např. headers, viz kód níže). Přidal jsem kvůli reportProgress
     * @param urlExpandParams urlExpandParams
     * @returns Data vrácená z API.
     */
    getoneRaw(id, params, cacheTimeout, options) {
        // V případech, kdy nemám id nadefinované, nechci zbytečně posílat request.
        // Nedává smysl volat getone s nenastaveným id, protože v endpointu to potom volá get místo getone
        if (id !== undefined && id !== null) {
            const url = this.getAllUrl(params);
            if (url === undefined) {
                console.error(`Chyba: třída ${this.constructor.name} metodu getone neumí - nemá definovanou vlastnost getallUrl`);
                return undefined;
            }
            const hash = id + ',' + this.getParamsHash(params);
            if (!this.cacheOne[hash]) {
                const opts = {
                    params: { ...params },
                    headers: { [CachingInterceptor.CACHE_HEADER]: cacheTimeout + '' },
                };
                if (options) {
                    Object.keys(options).forEach((key) => {
                        if (key !== 'params' && key !== 'headers') {
                            opts[key] = options[key];
                        }
                    });
                }
                this.cacheOne[hash] = this.http
                    .get(`${this.baseurl}/${url}` + `/${id}`, opts)
                    .pipe(tap((d) => {
                    delete this.cacheOne[hash];
                }))
                    .pipe(share());
            }
            return this.cacheOne[hash];
        }
        return of(null);
    }
    /**
     * Vrací jeden objekt, který je z principu singletonem
     * Obrací se na Api metodou get, ale vrací pouze jeden objekt, nikoli GetallResult
     *
     * @param params   Objekt s parametry, které budou přidány k URL jako query string.
     * @param cacheTimeout      Pocet sekund do expirace cache
     * @param urlExpandParams urlExpandParams
     * @returns Data vrácená z API.
     */
    getOneBy(params, cacheTimeout, urlExpandParams) {
        this.urlExpandParams = urlExpandParams;
        const url = this.getAllUrl(params);
        if (url === undefined) {
            console.error(`Chyba: třída ${this.constructor.name} metodu getOneBy neumí - nemá definovanou vlastnost getallUrl`);
            return undefined;
        }
        const hash = '' + this.getParamsHash(params);
        if (!this.cacheSingleton[hash]) {
            const opts = {
                params: { ...params },
                headers: { [CachingInterceptor.CACHE_HEADER]: cacheTimeout + '' },
            };
            this.cacheSingleton[hash] = this.http
                .get(`${this.baseurl}/${url}`, opts)
                .pipe(map((d) => {
                const res = d;
                delete this.cacheSingleton[hash];
                return this.recordPostprocess(res);
            }))
                .pipe(share());
        }
        return this.cacheSingleton[hash];
    }
    /**
     * @deprecated Use the 'getOneBy' function instead.
     */
    getSingleton(params, cacheTimeout, urlExpandParams) {
        console.warn("@deprecated Use the 'getOneBy' function instead.");
        return this.getOneBy(params, cacheTimeout, urlExpandParams);
    }
    /**
     * Upravuje načtená data jednoho záznamu. Tuto metodu typicky přetěžujte a provádějte v ní vlastní transformace data
     * jednoho záznamu.
     * @param record záznam jak byl načten z API.
     * @returns transformovaný záznam.
     */
    recordPostprocess(record) {
        return record;
    }
    /**
     * Uložení dat existujícího objektu.
     *
     * @param id  Id editovaného objektu
     * @param data   Editovaný objekt
     * @param params HTTP params
     * @param urlExpandParams
     * @returns Nová data objektu změněného editací
     */
    put(id, data, params, urlExpandParams) {
        this.urlExpandParams = urlExpandParams;
        const url = this.getAllUrl(params);
        if (url === undefined) {
            console.error(`Chyba: třída ${this.constructor.name} metodu put neumí - nemá definovanou vlastnost getallUrl`);
            return undefined;
        }
        // const opts = {params};
        this.clearCache();
        // TODO: dostat ty opts do query stringu URL jako GET parametry
        return this.http
            .put(`${this.baseurl}/${url}` + (id ? `/${id}` : ``), data, params)
            .pipe(map((d) => d), tap((x) => this.dataChanged()));
    }
    /**
     * Vytvoření nového objektu - nebo někdy editace singletonu (jako je profil uživatele).
     *
     * Tohle asi proberme, ale v běžné praxi s REST API metoda PUT přijímá
     * nějaké id - které u singletonů prostě není - takže se rozhodnout, co
     * je správnější (asi ve smyslu "více v souladu s konvencí" - přistupovat
     * k tomu na obou stranách jako k PUT a případné založení nového objektu
     * (které beztak - jako registrace nového uživatele - bude dost specifické)
     * může být nejpohodlnější a možná i nejprůhlednější.
     *
     * @param data   Editovaný objekt
     * @param params HTTP params.
     * @param urlExpandParams urlExpandParams
     * @returns Nová data objektu změněného editací
     */
    post(data, params, urlExpandParams) {
        this.urlExpandParams = urlExpandParams;
        const url = this.getAllUrl(params);
        if (url === undefined) {
            console.error(`Chyba: třída ${this.constructor.name} metodu post neumí - nemá definovanou vlastnost getallUrl`);
            return undefined;
        }
        this.clearCache();
        return this.http.post(`${this.baseurl}/${url}`, data, params).pipe(map((d) => d), tap((x) => this.dataChanged()));
    }
    /**
     * Vrátí pole options do populistu.
     * Primárně využívá sloupečky pojemnované id a name, pomocí funkce transformOptions lze nadefinovat data,
     * která se transformují na id a jméno
     * @param params parmetry
     * @param id id
     * @param cacheTimeout Pocet sekund do expirace cache
     * @param urlExpandParams urlExpandParams
     */
    optionsGet(params, id, cacheTimeout, urlExpandParams) {
        this.urlExpandParams = urlExpandParams;
        const url = this.getAllUrl(params);
        if (url === undefined) {
            console.error(`Chyba: třída ${this.constructor.name} metodu getall neumí - nemá definovanou vlastnost getallUrl`);
            return undefined;
        }
        const hash = '' + this.getParamsHash(params);
        if (!this.cacheOptions[hash]) {
            if (id) {
                return this.getoneRaw(id, params, cacheTimeout)
                    .pipe(tap((row) => {
                    delete this.cacheOptions[hash];
                }), map((row) => {
                    if (row) {
                        return [this.tranformOptions(row)];
                    }
                    else {
                        return [];
                    }
                }))
                    .pipe(share());
            }
            else {
                return this.getallRaw(params, cacheTimeout)
                    .pipe(tap((result) => {
                    delete this.cacheOptions[hash];
                }), map((result) => {
                    if (result.data) {
                        return result.data.map(this.tranformOptions);
                    }
                    else {
                        return [];
                    }
                }))
                    .pipe(share());
            }
        }
        return this.cacheOptions[hash];
    }
    /**
     * Transformuje data do formátu použitelného popuplistem. Defaultně využívá sloupečky s názvem id a name, ale je možné
     * v jednotlivých třídách přetížit a využít sloupečky s jinými názvy případně jejich kombinaci.
     * @param row data sloupečku
     */
    tranformOptions(row) {
        let name = row.name;
        if (row._localizations && row._localizations.name) {
            name = row._localizations.name;
        }
        return { id: row.id.toString(), name, disabled: row.disabled };
    }
    clearCache() {
        this.cache.clear(this.getAllUrl({}));
    }
    /**
     * Pokud je nastaveno na true, parametry getAll požadavku jsou posílány formou payload metody POST
     * @protected
     */
    getAllParamsAsPost() {
        return false;
    }
    dataChanged() {
        this.onDataChanged.emit({});
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: DataBase, deps: [{ token: i1.HttpClient }, { token: DataBaseServiceConfig }, { token: i3.CachingInterceptor }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: DataBase, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.7", ngImport: i0, type: DataBase, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: DataBaseServiceConfig }, { type: i3.CachingInterceptor }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DataBase, DataBaseServiceConfig, OgDataBaseModule };
